import { 
    Box, 
    Typography, 
    Paper, 
    Switch, 
    FormControlLabel, 
    Autocomplete, 
    TextField, 
    Button, 
    Chip, 
    Stack, 
    Grid, 
    Card, 
    CardContent, 
    IconButton,
    Divider,
    Alert,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Breadcrumbs,
    Radio,
    RadioGroup,
    FormLabel,
    Checkbox,
    duration,
    AlertTitle
} from "@mui/material";
import { 
    VideoCall, 
    Lock, 
    Public, 
    AccessTime, 
    CalendarMonth, 
    Add, 
    PlayArrow, 
    Schedule,
    Collections as CollectionsIcon,
    Edit,
    Save,
    NavigateNext,
    School,
    DesktopWindows,
    HourglassEmpty,
    ArrowBack
} from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useClassroomActions } from "../../../../hooks/useClassroomActions";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Lecture, newLecture, LectureAccess } from "../../../../models/lecture";
import { Collection } from "../../../../models/collection";
import axios from "axios";
import { useSnackbar } from "../../../../components/snackbar";
import PublicLectureAlertDialog from "../components/publc-lecture-alert-dialog";
import BackdropLoader from "../../../../components/backdrop-loader";
import { useDomainActions } from "../../../../hooks/useDomainActions";
import isHubActivated from "../../../../utils/is-hub-activated";
import getLocalTime from "../../../../utils/get-local-time";
import getLocalDateTime from "../../../../utils/get-local-date-time";
import formatDateTime from "../../../../utils/get-local-date-time";

type RouteParams = {
    classroom_id: string;
    lecture_id: string;
};

const API_URL = process.env.REACT_APP_API_URL;

const getLectureStatus = (lecture: Lecture) => {
    if (!lecture?.start_time) {
        return { canEdit: true }
    }
    
    const now = new Date().getTime();
    const startTime = new Date(lecture.start_time).getTime();
    const endTime = startTime + (lecture.duration * 60 * 1000);
    const tenMinutesBefore = startTime - (10 * 60 * 1000);
    
    const canEdit = now < tenMinutesBefore;
    const isLive = now >= startTime && now <= endTime;
    const isAwaiting = now < startTime;
    const isEnded = now > endTime;
    
    return { canEdit, isLive, isAwaiting, isEnded };
};


function LectureDetails() {
    const { classroom_id, lecture_id } = useParams<RouteParams>();
    const { 
        classrooms, 
        getClassrooms, 
        getLectures, 
        getCollections,
        updateLecture
    } = useClassroomActions();

    const navigate = useNavigate();

    const { hub, getHub } = useDomainActions();
 
    const classroom = classrooms?.[Number(classroom_id)];
    const lectures = classroom?.lectures;
    const collections = classroom?.collections;
    const lecture = lectures?.[Number(lecture_id)];
    
    const [loading, setLoading] = useState(true);
    const [locLecture, setLocLecture] = useState<Lecture>(newLecture());
    const [selectedDate, setSelectedDate] = useState<string>("");
    const [selectedTime, setSelectedTime] = useState<string>("");
    const [isScheduling, setIsScheduling] = useState(false);

    const [isUpdating, setIsUpdating] = useState(false);
    
    const [joining, setJoining] = useState(false);

    const [changeAccessDialog, setChangeAccessDialog] = useState(false);
    
    const [timeSlots, setTimeSlots] = useState<[]>([]);

    const collectionsArray = collections ? Object.values(collections) : [];

    const lectureStatus = getLectureStatus(locLecture);

    const isScheduleDisabled = !selectedDate || !selectedTime || (locLecture && locLecture.duration < 30) || !locLecture?.title || isUpdating || isScheduling || !lectureStatus.canEdit;

    const today = new Date().toISOString().split('T')[0];

    const startTimeFormatted = formatDateTime(locLecture.start_time);

    const { showMessage } = useSnackbar();


    const handleLectureChange = (field: keyof Lecture, value: any) => {
        if (!locLecture || !lectureStatus.canEdit) return;          
        
        setLocLecture(prev => ({
            ...prev!,
            [field]: value
        }));
    };

    const handleRecordToggle = (checked: boolean) => {
        if (!lectureStatus.canEdit) return;
        handleLectureChange("record", checked);
        if (!checked) {
            handleLectureChange("collection_id", 0);
        }
    };

    const handleAccessChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!lectureStatus.canEdit) return;
        const value = parseInt(event.target.value) as LectureAccess;
        handleLectureChange("access", value);
    }

    const handleCollectionChange = (collection: Collection | null) => {
        if (!lectureStatus.canEdit) return;
        handleLectureChange("collection_id", collection ? collection.id : 0);
    };

    const handleSchedule = async () => {
        if (!locLecture || !lecture) return;

        if (!isHubActivated(hub)) {
            showMessage('Hub is not activated', 1);
            return;
        }
        
        if (lecture.access === LectureAccess.PRIVATE && locLecture.access == LectureAccess.PUBLIC) {
            setChangeAccessDialog(true);
        } else {
            handleLectureUpdate();
        }
    };

    const handleJoinLecture = async () => {
        if (!getLectureStatus(locLecture).isLive) {
            showMessage('not allowed', 1);
            return;
        }

        try {
            setJoining(true);
            const response = await axios.get<string>(
                `${API_URL}/instructor/classrooms/${classroom_id}/lectures/${lecture_id}/meeting-url`,
                { withCredentials: true }
            );

            window.open(response.data, '_blank');
        } catch (error) {
            handleApiError(error);
        } finally {
            setJoining(false);
        }
    };

    
    const handleLectureUpdate = async () => {
        if (!locLecture || !lectureStatus.canEdit) return;
        
        if (locLecture.record && !locLecture.collection_id) {
            showMessage('select a collection to save recording', 1);
            return;
        }

        setIsUpdating(true);
        const startTime = `${selectedDate} ${selectedTime.split("-")[0]}`

        locLecture.start_time = startTime.trimEnd();


        await updateLecture(Number(classroom_id), locLecture);

        setTimeSlots([]);
        setSelectedDate("");
        setSelectedTime("");
        setIsUpdating(false);

        setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100)
    };

    const handleApiError = (error: any) => {
        
        if (error?.response?.status === 401) {
            navigate("/sign-in");
            return;
        }

        const message =
            error?.response?.data?.detail ||
            error?.response?.data?.message ||
            error.message ||
            "An unexpected error occurred";

        showMessage(message, 0);
    };

    useEffect(() => {
        const loadCollections = async () => {
            if (!collections && locLecture.record) await getCollections(Number(classroom_id));
        };
        loadCollections();
    }, [locLecture.record]);

    useEffect(() => {
        const loadAvailableTimes = async () => {
            if (!lectureStatus.canEdit) return;
            
            if (!selectedDate) return;

            if (locLecture && (locLecture.duration < 30 || locLecture.duration > 300)) {
                return;
            }

            
            try {
                setTimeSlots([]);
                setIsScheduling(true);
                const response = await axios.get(
                    `${API_URL}/utils/instructors/meeting-slots?duration=${locLecture?.duration}&date=${selectedDate}&expected_students=${locLecture?.expected_students}`,
                    { withCredentials: true }
                );
                
                if (response.data.length < 1) {
                    showMessage('No available time for that date, please select another different date', 1);
                }
                setSelectedTime("");
                setTimeSlots(response.data);
                
            } catch (error) {
                handleApiError(error);
            } finally {
                setIsScheduling(false);
            }
        };

        loadAvailableTimes();
    }, [locLecture?.duration, selectedDate, lectureStatus.canEdit]);

    useEffect(() => {
        if (lecture) setLocLecture(lecture);
    }, [lecture]);
    
    useEffect(() => {
        const loadData = async () => {
            if (lecture) {
                setLoading(false);
                return;
            }
            
            if (!hub) await getHub();

            if (!classrooms) await getClassrooms();

            if (!lectures) {
                await getLectures(Number(classroom_id));
                setLoading(false);
            };
        }

        loadData();
    }, [classrooms, classroom_id, lecture_id]);

    if (!hub || loading || !lecture) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <BackdropLoader />
            </Box>
        );
    }


    return (
        <Box sx={{
            backgroundColor: "grey.100",
            minHeight: "100vh"
        }}>
             <Box 
                sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    padding: { xs: 1.5, sm: 2 },
                    backgroundColor: '#1e293b', // slate-800
                    borderBottom: '1px solid rgba(148, 163, 184, 0.2)',
                    backdropFilter: 'blur(8px)',
                    position: 'relative',
                }}
            >
                <Box
                    component={Link}
                    to={`/classroom/${classroom_id}#lectures`}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        textDecoration: 'none',
                        color: '#475569',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        transition: 'all 0.2s ease',
                        minWidth: 0,
                        flex: 1,
                    }}
                >
                    <ArrowBack 
                        fontSize="small" 
                        sx={{ 
                            flexShrink: 0,
                            color: '#64748b',
                            transition: 'color 0.2s ease',
                        }} 
                    />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                            variant="body2"
                            sx={{
                                fontSize: '0.75rem',
                                fontWeight: 400,
                                color: '#64748b',
                                lineHeight: 1,
                                mb: 0.25,
                            }}
                        >
                            Lectures
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                fontWeight: 600,
                                color: '#f1f5f9',
                                lineHeight: 1.2,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {lecture.title || "Untitled Lecture"}
                        </Typography>
                    </Box>
                </Box>

            </Box>

            <Box
                sx={{
                    maxWidth: 800,
                    mx: 'auto',
                    minHeight: '100vh',
                    paddingY: 3
                }}
            >
   
                <Alert 
                    severity="info" 
                    sx={{ 
                        mb: 4,
                        marginX: 2,
                        borderRadius: 3,
                        backgroundColor: '#eff6ff',
                        border: '1px solid #bfdbfe',
                        '& .MuiAlert-icon': { color: '#3b82f6' }
                    }}
                >
                    <AlertTitle sx={{ fontWeight: 600 }}>Development Notice</AlertTitle>
                    This feature is actively under development and is limited to 1000 students in a public lecture.
                </Alert>

                <Paper 
                    elevation={0}
                    sx={{ 
                        margin: 1,
                        borderRadius: 4,
                        border: '1px solid #e2e8f0',
                        overflow: 'hidden',
                        backgroundColor: 'white'
                    }}
                >
                   {startTimeFormatted && (
                        <Box 
                            sx={{ 
                                p: 3,
                                backgroundColor: lectureStatus.isLive ? '#ecfdf5' : '#fafafa',
                                borderBottom: '1px solid #e2e8f0',
                                background: lectureStatus.isLive 
                                    ? 'linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)'
                                    : 'linear-gradient(135deg, #fafafa 0%, #f4f4f5 100%)'
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
                                <Box 
                                    sx={{ 
                                        p: 1.5,
                                        borderRadius: 2,
                                        backgroundColor: lectureStatus.isLive ? '#10b981' : '#6b7280',
                                        color: 'white'
                                    }}
                                >
                                    <AccessTime sx={{ fontSize: 24 }} />
                                </Box>
                                
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="h6" fontWeight={600} color="#1e293b">
                                        Lecture Schedule
                                    </Typography>
                                    <Typography variant="body2" color="#64748b">
                                        {startTimeFormatted.date}
                                    </Typography>
                                </Box>

                                {lectureStatus.isLive && (
                                    <Chip 
                                        label="LIVE NOW" 
                                        sx={{ 
                                            backgroundColor: '#ef4444', 
                                            color: 'white',
                                            fontWeight: 700,
                                            fontSize: '0.75rem',
                                            height: 32,
                                            animation: 'pulse 2s infinite',
                                            boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)'
                                        }} 
                                    />
                                )}
                                
                                {lectureStatus.isAwaiting && (
                                    <Chip 
                                        label="Starting Soon" 
                                        sx={{ 
                                            backgroundColor: '#f59e0b',
                                            color: 'white',
                                            fontWeight: 600,
                                            height: 32
                                        }} 
                                    />
                                )}

                                {lectureStatus.isEnded && (
                                    <Chip 
                                        label="Completed" 
                                        sx={{ 
                                            backgroundColor: '#6b7280',
                                            color: 'white',
                                            fontWeight: 600,
                                            height: 32
                                        }} 
                                    />
                                )}
                            </Box>

                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Box 
                                    sx={{ 
                                        flex: 1,
                                        textAlign: 'center',
                                        p: 2,
                                        borderRadius: 2,
                                        backgroundColor: 'white',
                                        border: '1px solid #e2e8f0'
                                    }}
                                >
                                    <Typography variant="caption" color="#64748b" fontWeight={500}>
                                        Start Time
                                    </Typography>
                                    <Typography variant="h6" fontWeight={700} color="#1e293b">
                                        {startTimeFormatted.time}
                                    </Typography>
                                </Box>
                                <Box 
                                    sx={{ 
                                        flex: 1,
                                        textAlign: 'center',
                                        p: 2,
                                        borderRadius: 2,
                                        backgroundColor: 'white',
                                        border: '1px solid #e2e8f0'
                                    }}
                                >
                                    <Typography variant="caption" color="#64748b" fontWeight={500}>
                                        Duration
                                    </Typography>
                                    <Typography variant="h6" fontWeight={700} color="#1e293b">
                                        {locLecture.duration} mins
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    )}

                    <Box sx={{ p: 2 }}>
                        {/* Editing disabled alert */}
                        {!lectureStatus.canEdit && locLecture.start_time && (
                            <Alert 
                                severity="warning" 
                                sx={{ 
                                    mb: 4,
                                    borderRadius: 3,
                                    backgroundColor: '#fffbeb',
                                    border: '1px solid #fed7aa',
                                    '& .MuiAlert-icon': { color: '#f59e0b' }
                                }}
                            >
                                <AlertTitle sx={{ fontWeight: 600 }}>Editing Restricted</AlertTitle>
                                Lecture details cannot be modified within 10 minutes of start time or during/after the lecture.
                            </Alert>
                        )}

                        <Stack spacing={4}>
                            {/* Lecture Title */}
                            <TextField
                                label="Lecture Title"
                                value={locLecture.title}
                                onChange={(e) => handleLectureChange("title", e.target.value)}
                                fullWidth
                                disabled={!lectureStatus.canEdit}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 3,
                                        backgroundColor: 'white',
                                        fontSize: '1.1rem',
                                        '&:hover fieldset': {
                                            borderColor: '#10b981',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#10b981',
                                            borderWidth: 2,
                                        }
                                    },
                                    '& .MuiInputLabel-root.Mui-focused': {
                                        color: '#10b981',
                                        fontWeight: 600
                                    },
                                }}
                            />

                            {/* Lecture Access */}
                            <Paper 
                                elevation={0} 
                                sx={{ 
                                    p: 3, 
                                    backgroundColor: '#f8fafc', 
                                    borderRadius: 3,
                                    border: '1px solid #e2e8f0',
                                    opacity: !lectureStatus.canEdit ? 0.6 : 1,
                                }}
                            >
                                <FormControl component="fieldset" disabled={!lectureStatus.canEdit} fullWidth>
                                    <FormLabel 
                                        component="legend" 
                                        sx={{ 
                                            color: '#374151',
                                            fontSize: '1rem',
                                            fontWeight: 600,
                                            mb: 2,
                                            '&.Mui-focused': {
                                                color: '#10b981',
                                            }
                                        }}
                                    >
                                        Lecture Access
                                    </FormLabel>
                                    <RadioGroup
                                        value={locLecture.access.toString()}
                                        onChange={handleAccessChange}
                                        sx={{ gap: 1 }}
                                    >
                                        <FormControlLabel
                                            value={LectureAccess.PRIVATE.toString()}
                                            control={
                                                <Radio 
                                                    sx={{
                                                        color: '#9ca3af',
                                                        '&.Mui-checked': {
                                                            color: '#10b981',
                                                        }
                                                    }}
                                                />
                                            }
                                            label={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Box 
                                                        sx={{ 
                                                            p: 1, 
                                                            borderRadius: 2, 
                                                            backgroundColor: 'white',
                                                            border: '1px solid #e2e8f0'
                                                        }}
                                                    >
                                                        <Lock sx={{ fontSize: 20, color: '#6b7280' }} />
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="body1" fontWeight={600} color="#1e293b">
                                                            Private
                                                        </Typography>
                                                        <Typography variant="caption" color="#64748b">
                                                            Only you can access this lecture
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            }
                                            sx={{ 
                                                mx: 0,
                                                p: 2,
                                                borderRadius: 2,
                                                backgroundColor: 'white',
                                                border: '1px solid #e2e8f0',
                                                mb: 1,
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    backgroundColor: lectureStatus.canEdit ? '#f0fdf4' : 'white',
                                                    borderColor: lectureStatus.canEdit ? '#10b981' : '#e2e8f0'
                                                }
                                            }}
                                        />
                                        <FormControlLabel
                                            value={LectureAccess.PUBLIC.toString()}
                                            control={
                                                <Radio 
                                                    sx={{
                                                        color: '#9ca3af',
                                                        '&.Mui-checked': {
                                                            color: '#10b981',
                                                        }
                                                    }}
                                                />
                                            }
                                            label={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Box 
                                                        sx={{ 
                                                            p: 1, 
                                                            borderRadius: 2, 
                                                            backgroundColor: 'white',
                                                            border: '1px solid #e2e8f0'
                                                        }}
                                                    >
                                                        <Public sx={{ fontSize: 20, color: '#6b7280' }} />
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="body1" fontWeight={600} color="#1e293b">
                                                            Public
                                                        </Typography>
                                                        <Typography variant="caption" color="#64748b">
                                                            Your students can access this lecture
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            }
                                            sx={{ 
                                                mx: 0,
                                                p: 2,
                                                borderRadius: 2,
                                                backgroundColor: 'white',
                                                border: '1px solid #e2e8f0',
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    backgroundColor: lectureStatus.canEdit ? '#f0fdf4' : 'white',
                                                    borderColor: lectureStatus.canEdit ? '#10b981' : '#e2e8f0'
                                                }
                                            }}
                                        />
                                    </RadioGroup>
                                </FormControl>
                            </Paper>

                            {/* Recording Options */}
                            <Paper 
                                elevation={0}
                                sx={{ 
                                    p: 3,
                                    backgroundColor: '#f8fafc',
                                    borderRadius: 3,
                                    border: '1px solid #e2e8f0',
                                    opacity: !lectureStatus.canEdit ? 0.6 : 1
                                }}
                            >
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={locLecture.record}
                                            onChange={(e) => handleRecordToggle(e.target.checked)}
                                            disabled={!lectureStatus.canEdit}
                                            sx={{
                                                color: '#10b981',
                                                '&.Mui-checked': {
                                                    color: '#10b981',
                                                },
                                            }}
                                        />
                                    }
                                    label={
                                        <Box>
                                            <Typography variant="body1" fontWeight={600} color="#1e293b">
                                                Record this lecture
                                            </Typography>
                                            <Typography variant="body2" color="#64748b">
                                                Save recording to a collection for later access by students
                                            </Typography>
                                        </Box>
                                    }
                                    sx={{ alignItems: 'flex-start' }}
                                />

                                {locLecture.record && (
                                    <Box 
                                        sx={{ 
                                            mt: 3, 
                                            display: 'flex', 
                                            gap: 2, 
                                            justifyContent: 'center', 
                                            alignItems: 'center' 
                                        }}
                                    >
                                        {!collections ? (
                                            <CircularProgress size={18}/>
                                        ) : (
                                            <Autocomplete
                                                options={collectionsArray}
                                                getOptionLabel={(option) => option.title}
                                                value={collectionsArray.find(c => c.id === locLecture.collection_id)}
                                                onChange={(_, value) => handleCollectionChange(value)}
                                                disabled={!lectureStatus.canEdit}
                                                sx={{ flexGrow: 1 }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="Select Collection"
                                                        placeholder="Search collections..."
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                borderRadius: 3,
                                                                backgroundColor: "white"
                                                            },
                                                            '& .MuiInputLabel-root.Mui-focused': {
                                                                color: '#10b981',
                                                            },
                                                        }}
                                                    />
                                                )}
                                            />
                                        )}
                                        
                                    </Box>
                                )}
                            </Paper>

                            {lectureStatus.canEdit && (
                                <Paper 
                                    elevation={0}
                                    sx={{ 
                                        p: 3,
                                        backgroundColor: '#f8fafc',
                                        borderRadius: 3,
                                        border: '1px solid #e2e8f0'
                                    }}
                                >
                                    <Typography variant="h6" fontWeight={600} color="#1e293b" mb={3}>
                                        Schedule Settings
                                    </Typography>
                                    
                                    <Grid container spacing={3} mb={3}>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <TextField
                                                type="date"
                                                label="Select Date"
                                                value={selectedDate}
                                                onChange={(e) => setSelectedDate(e.target.value)}
                                                InputLabelProps={{ shrink: true }}
                                                inputProps={{ min: today }}
                                                fullWidth
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 3,
                                                        backgroundColor: 'white'
                                                    },
                                                    '& .MuiInputLabel-root.Mui-focused': {
                                                        color: '#10b981',
                                                    },
                                                }}
                                            />
                                        </Grid>

                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <TextField
                                                label="Duration (minutes)"
                                                type="number"
                                                value={locLecture.duration}
                                                onChange={(e) => handleLectureChange("duration", e.target.value)}
                                                fullWidth
                                                helperText="Min: 30; Max: 300"
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 3,
                                                        backgroundColor: 'white'
                                                    },
                                                    '& .MuiInputLabel-root.Mui-focused': {
                                                        color: '#10b981',
                                                    },
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                    
                                    {isScheduling && (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                                            <CircularProgress sx={{ color: '#10b981' }} />
                                        </Box>
                                    )}

                                    {timeSlots.length > 0 && (
                                        <Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                                <Typography variant="subtitle1" fontWeight={600} color="#1e293b">
                                                    Available Time Slots ({timeSlots.length})
                                                </Typography>
                                                {selectedTime && (
                                                    <Chip 
                                                        label={`Selected: ${getLocalTime(selectedTime)}`}
                                                        sx={{ 
                                                            backgroundColor: '#10b981',
                                                            color: 'white',
                                                            fontWeight: 600
                                                        }} 
                                                    />
                                                )}
                                            </Box>
                                            
                                            {/* Scrollable container for time slots */}
                                            <Box 
                                                sx={{ 
                                                    maxHeight: { xs: 200, sm: 250, md: 300 },
                                                    overflowY: 'auto',
                                                    pr: 1,
                                                    '&::-webkit-scrollbar': {
                                                        width: '6px',
                                                    },
                                                    '&::-webkit-scrollbar-track': {
                                                        backgroundColor: '#f1f5f9',
                                                        borderRadius: '3px',
                                                    },
                                                    '&::-webkit-scrollbar-thumb': {
                                                        backgroundColor: '#cbd5e1',
                                                        borderRadius: '3px',
                                                        '&:hover': {
                                                            backgroundColor: '#94a3b8',
                                                        },
                                                    },
                                                }}
                                            >
                                                <Grid container spacing={2}>
                                                    {timeSlots.map((time) => (
                                                        <Grid size={{ xs: 6, sm: 4, md: 3 }} key={time}>
                                                            <Button
                                                                variant={selectedTime === time ? "contained" : "outlined"}
                                                                fullWidth
                                                                onClick={() => setSelectedTime(time)}
                                                                sx={{
                                                                    borderRadius: 3,
                                                                    py: 1.5,
                                                                    fontWeight: 600,
                                                                    ...(selectedTime === time ? {
                                                                        backgroundColor: '#10b981',
                                                                        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                                                                        '&:hover': {
                                                                            backgroundColor: '#059669',
                                                                        }
                                                                    } : {
                                                                        backgroundColor: "white",
                                                                        borderColor: '#e2e8f0',
                                                                        color: '#374151',
                                                                        '&:hover': {
                                                                            borderColor: '#10b981',
                                                                            backgroundColor: '#f0fdf4',
                                                                        }
                                                                    })
                                                                }}
                                                            >
                                                                {getLocalTime(time)}
                                                            </Button>
                                                        </Grid>
                                                    ))}
                                                </Grid>
                                            </Box>
                                            
                                            {timeSlots.length > 12 && (
                                                <Typography variant="caption" color="#64748b" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                                                    Scroll to see more time slots
                                                </Typography>
                                            )}
                                        </Box>
                                    )}
                                </Paper>
                            )}

                        </Stack>
                    </Box>
                </Paper>

                {(lectureStatus.isLive || lectureStatus.canEdit) && (
                    <Box sx={{ mt: 4, textAlign: 'center' }}>
                        {lectureStatus.isLive ? (
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={<VideoCall />}
                                onClick={handleJoinLecture}
                                disabled={lectureStatus.isAwaiting || joining}
                                sx={{
                                    backgroundColor: '#10b981',
                                    color: 'white',
                                    borderRadius: 3,
                                    fontSize: '1rem',
                                    fontWeight: 700,
                                    boxShadow: '0 8px 25px rgba(16, 185, 129, 0.3)',
                                    '&:hover': {
                                        backgroundColor: '#059669',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 12px 35px rgba(16, 185, 129, 0.4)',
                                    },
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                {joining ? <CircularProgress color="inherit" size={24} /> : "Join Lecture"}
                            </Button>
                        ) : (
                            <Button 
                                variant="contained"
                                size="large"
                                onClick={handleSchedule}
                                disabled={isScheduleDisabled || isUpdating}
                                startIcon={<Schedule />}
                                sx={{
                                    backgroundColor: '#10b981',
                                    color: 'white',
                                    
                                    borderRadius: 3,
                                    fontSize: '1rem',
                                    fontWeight: 700,
                                    boxShadow: '0 8px 25px rgba(16, 185, 129, 0.3)',
                                    '&:hover': {
                                        backgroundColor: '#059669',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 12px 35px rgba(16, 185, 129, 0.4)',
                                    },
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                {isUpdating ? <CircularProgress color="inherit" size={14}/> : "Schedule Lecture"}
                            </Button>
                        )}
                    </Box>
                )}
            </Box>

            <PublicLectureAlertDialog
                open={changeAccessDialog}
                setOpen={setChangeAccessDialog}
                updateFunction={handleLectureUpdate}
            />

            {isUpdating && <BackdropLoader/>}
        </Box>
    );
}

export default LectureDetails;